import { Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core'
import { User } from '../../interfaces/user.interface'
import { Player } from '../../../players/interfaces/player.interface'
import { AvatarService } from '../../services/avatar.service'

export type AvatarEntity = User | Player | {
  name: string;
  image_url?: string;
  email?: string
};

@Component({
  selector: 'app-avatar',
  standalone: true,
  imports: [],
  templateUrl: './avatar.component.html',
  styleUrl: './avatar.component.scss',
})
export class AvatarComponent implements OnInit, OnChanges {
  @Input() entity!: AvatarEntity
  @Input() size: number = 40
  @Input() showInitials: boolean = true

  imageUrl: string = ''
  initials: string = ''
  backgroundColor: string = ''
  showImage: boolean = false
  imageLoaded: boolean = false
  imageError: boolean = false

  private avatarService = inject(AvatarService)

  ngOnInit (): void {
    if (this.entity) {
      this.setupAvatar()
    }
  }

  /** A new photo (or none) on the same person: start over, so the new image shows. */
  ngOnChanges (changes: SimpleChanges): void {
    if (changes['entity'] && !changes['entity'].firstChange && this.entity) {
      this.imageError = false
      this.imageLoaded = false
      this.showImage = false
      this.setupAvatar()
    }
  }

  getEntityName (): string {
    if (this.isUser(this.entity)) {
      return `${this.entity.first_name} ${this.entity.last_name}`
    }
    return this.entity.name || 'User'
  }

  onImageLoad (): void {
    this.imageLoaded = true
    this.showImage = true
    this.imageError = false
  }

  onImageError (): void {
    this.imageError = true
    this.showImage = false
    this.imageLoaded = false
  }

  private setupAvatar (): void {
    if (this.isUser(this.entity)) {
      // User entity - use Gravatar with email
      this.imageUrl = this.avatarService.getAvatarUrl(this.entity, this.size)
      this.initials = this.avatarService.getUserInitials(this.entity)
      this.backgroundColor = this.avatarService.getInitialsBackgroundColor(
        this.entity.email)
    } else {
      // Player or generic entity - use image_url if available
      this.imageUrl = this.entity.image_url || ''
      this.initials = this.getEntityInitials(this.entity)
      this.backgroundColor = this.avatarService.getInitialsBackgroundColor(
        this.getEntityEmail(this.entity) || this.entity.name || '',
      )
    }
  }

  private isUser (entity: AvatarEntity): entity is User {
    return 'email' in entity && 'first_name' in entity && 'last_name' in entity
  }

  private getEntityInitials (entity: AvatarEntity): string {
    if (this.isUser(entity)) {
      return this.avatarService.getUserInitials(entity)
    }

    // For players and generic entities, extract initials from name
    const name = entity.name || ''
    const names = name.split(' ').filter(n => n.length > 0)

    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase()
    } else if (names.length === 1) {
      return names[0].substring(0, 2).toUpperCase()
    }

    return 'U' // Default fallback
  }

  private getEntityEmail (entity: AvatarEntity): string | undefined {
    return 'email' in entity ? entity.email : undefined
  }
}
